import { useState, useRef, useEffect } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  SunIcon,
  MoonIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  BookmarkSquareIcon,
  BookOpenIcon,
  PencilSquareIcon,
  MapPinIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { LogoIcon, Avatar } from '../ui';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import { getUserProfile } from '../../services/firebase';
import { getUnreadNotificationCount } from '../../services/firebase/templateSharing';
import { NotificationsPanel } from '../features/sponsor/NotificationsPanel';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const dailyItems = [
  { name: 'Home',            path: '/dashboard',      icon: HomeIcon },
  { name: 'Morning Check-in',path: '/checkin',         icon: SunIcon },
  { name: 'Nightly Review',  path: '/nightly-review',  icon: MoonIcon },
  { name: 'Activity Log',    path: '/activity-log',    icon: ClipboardDocumentListIcon },
  { name: 'Gratitude',       path: '/checkin',         icon: HeartIcon },
];

const toolItems = [
  { name: 'Step Work',         path: '/steps',               icon: BookmarkSquareIcon },
  { name: 'Read',              path: '/read',                icon: BookOpenIcon },
  { name: 'Journal',           path: '/journal',             icon: PencilSquareIcon },
  { name: 'Find Meetings',     path: '/meetings',            icon: MapPinIcon },
  { name: 'Sponsor Dashboard', path: '/sponsor-dashboard',   icon: UserGroupIcon },
];

function NavItem({
  item,
  isCollapsed,
}: {
  item: { name: string; path: string; icon: ComponentType<SVGProps<SVGSVGElement>> };
  isCollapsed: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      title={isCollapsed ? item.name : undefined}
      className={({ isActive }) =>
        `relative flex items-center gap-3 px-3 py-2.5 rounded-lg
         font-medium text-sm transition-all duration-150 group
         ${isCollapsed ? 'justify-center' : ''}
         ${
           isActive
             ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 border-l-2 border-teal-500'
             : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-navy-800 dark:hover:text-surface-100 border-l-2 border-transparent'
         }`
      }
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!isCollapsed && <span className="truncate">{item.name}</span>}
    </NavLink>
  );
}

export function Sidebar({ isCollapsed, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { resolvedTheme, setTheme } = useThemeStore();
  const [firstName, setFirstName] = useState('');
  const [lastInitial, setLastInitial] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user?.uid) return;
    getUserProfile(user.uid).then(profile => {
      if (profile) {
        setFirstName(profile.firstName);
        setLastInitial(profile.lastInitial ?? '');
      }
    }).catch(console.error);
  }, [user?.uid]);

  // Load unread notification count and poll every 30s
  useEffect(() => {
    if (!user?.uid) return;
    const load = () => getUnreadNotificationCount(user.uid).then(setUnreadCount).catch(console.error);
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [user?.uid]);

  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    }
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      await logout();
    } catch {
      // authStore clears state on error; always navigate
    }
    navigate('/login');
  };

  const toggleTheme = () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');

  const displayName = firstName
    ? lastInitial ? `${firstName} ${lastInitial}.` : firstName
    : user?.email?.split('@')[0] || 'Friend';
  const initials = firstName
    ? `${firstName[0]}${lastInitial || ''}`.toUpperCase()
    : (user?.email?.[0] ?? '?').toUpperCase();

  return (
    <>
      <aside
        className={`
          hidden md:flex flex-col
          fixed inset-y-0 left-0 z-50
          bg-white dark:bg-surface-900
          border-r border-surface-200 dark:border-surface-800
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-16' : 'w-60'}
        `}
      >
        {/* ── Branding ── */}
        <div className={`flex items-center gap-3 px-3 pt-5 pb-4 border-b border-surface-100 dark:border-surface-800 ${isCollapsed ? 'justify-center' : ''}`}>
          <LogoIcon size="md" className="flex-shrink-0" />
          {!isCollapsed && (
            <span className="font-display font-semibold text-base text-navy-800 dark:text-gold-500 truncate">
              Daily Reprieve
            </span>
          )}
        </div>

        {/* ── User Profile ── */}
        <div className="relative px-3 py-3 border-b border-surface-100 dark:border-surface-800" ref={userMenuRef}>
          {/* Row: avatar button + bell */}
          <div className={`flex items-center gap-1 ${isCollapsed ? 'flex-col' : ''}`}>
            <button
              onClick={() => setShowUserMenu(v => !v)}
              aria-label={`${displayName} — open user menu`}
              aria-expanded={showUserMenu}
              className={`flex items-center gap-3 p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors ${isCollapsed ? 'justify-center w-full' : 'flex-1 min-w-0'}`}
            >
              <Avatar name={initials} size="sm" variant="brand" className="flex-shrink-0" />
              {!isCollapsed && (
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-navy-800 dark:text-surface-100 truncate">{displayName}</p>
                </div>
              )}
            </button>

            {/* Bell icon — separate from user menu button */}
            <button
              onClick={() => setShowNotifications(true)}
              aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
              className="relative flex-shrink-0 p-2 rounded-lg text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-700 dark:hover:text-surface-200 transition-colors"
            >
              <BellIcon className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-teal-500 text-white text-[10px] font-semibold rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* User menu popover */}
          <AnimatePresence>
            {showUserMenu && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] as const }}
                className={`absolute z-10 mt-1 bg-white dark:bg-surface-800 rounded-xl shadow-lg border border-surface-200 dark:border-surface-700 overflow-hidden min-w-[160px] ${isCollapsed ? 'left-16 top-2' : 'left-3 right-3 top-full'}`}
              >
                <NavLink
                  to="/settings"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
                >
                  <Cog6ToothIcon className="w-4 h-4" />
                  Settings
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-danger-600 dark:text-danger-400 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  Sign Out
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          {/* Daily Practice group */}
          <div>
            {!isCollapsed && (
              <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-surface-400 dark:text-surface-500">
                Daily Practice
              </p>
            )}
            <div className="space-y-0.5">
              {dailyItems.map(item => (
                <NavItem key={item.path} item={item} isCollapsed={isCollapsed} />
              ))}
            </div>
          </div>

          {/* Tools & Community group */}
          <div>
            {!isCollapsed && (
              <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-surface-400 dark:text-surface-500">
                Tools & Community
              </p>
            )}
            <div className="space-y-0.5">
              {toolItems.map(item => (
                <NavItem key={item.path} item={item} isCollapsed={isCollapsed} />
              ))}
            </div>
          </div>
        </nav>

        {/* ── Dark Mode Toggle ── */}
        <div className="px-3 pb-1 border-t border-surface-100 dark:border-surface-800 pt-3">
          <button
            onClick={toggleTheme}
            aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-700 dark:hover:text-surface-200 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
          >
            {resolvedTheme === 'dark' ? (
              <SunIcon className="w-5 h-5 flex-shrink-0" />
            ) : (
              <MoonIcon className="w-5 h-5 flex-shrink-0" />
            )}
            {!isCollapsed && (
              <span>{resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
            )}
          </button>
        </div>

        {/* ── Collapse Toggle ── */}
        <div className="px-3 pb-4 pt-1">
          <button
            onClick={onToggle}
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-700 dark:hover:text-surface-200 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeftIcon className="w-5 h-5" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Notifications panel — rendered outside aside so it covers full viewport */}
      {user?.uid && (
        <NotificationsPanel
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          userId={user.uid}
          onRefresh={async () => {
            const count = await getUnreadNotificationCount(user.uid);
            setUnreadCount(count);
          }}
        />
      )}
    </>
  );
}

export default Sidebar;
